/**
 * Status Utility for Landscape
 * Provides consistent labeling, styling, and icons for all entity statuses across the platform.
 * v2: Standardized for High-end Landscape Construction & Maintenance
 */

// --- Types ---
export interface StatusConfig {
  label: string;
  variant: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'purple' | 'orange' | 'pink' | 'secondary';
  icon: string;
}

// --- Configurations ---

/** Account / User active status */
export const accountStatusMap: Record<string, StatusConfig> = {
  active: { label: 'Hoạt động', variant: 'success', icon: 'ri-checkbox-circle-line' },
  locked: { label: 'Bị khóa', variant: 'danger', icon: 'ri-lock-2-line' },
  pending: { label: 'Chờ kích hoạt', variant: 'warning', icon: 'ri-time-line' },
};

/** Employee working status */
export const workStatusMap: Record<string, StatusConfig> = {
  working: { label: 'Đang làm việc', variant: 'success', icon: 'ri-briefcase-line' },
  on_leave: { label: 'Đang nghỉ', variant: 'warning', icon: 'ri-home-heart-line' },
  resigned: { label: 'Đã nghỉ việc', variant: 'danger', icon: 'ri-user-unfollow-line' },
};

/** Work Order / Task status */
export const woStatusMap: Record<string, StatusConfig> = {
  open: { label: 'Mới', variant: 'info', icon: 'ri-folder-open-line' },
  pending: { label: 'Chờ duyệt', variant: 'warning', icon: 'ri-time-line' },
  in_progress: { label: 'Đang thực hiện', variant: 'orange', icon: 'ri-settings-4-line' },
  completed: { label: 'Hoàn thành', variant: 'success', icon: 'ri-checkbox-circle-line' },
  cancelled: { label: 'Đã hủy', variant: 'danger', icon: 'ri-close-circle-line' },
  overdue: { label: 'Quá hạn', variant: 'danger', icon: 'ri-error-warning-line' },
};

/** Approval flow status */
export const approvalStatusMap: Record<string, StatusConfig> = {
  pending: { label: 'Chờ duyệt', variant: 'info', icon: 'ri-time-line' },
  approved: { label: 'Đã duyệt', variant: 'success', icon: 'ri-check-line' },
  rejected: { label: 'Từ chối', variant: 'danger', icon: 'ri-close-line' },
  returned: { label: 'Yêu cầu chỉnh sửa', variant: 'warning', icon: 'ri-arrow-go-back-line' },
};

/** Project status - Specialized for Landscape Projects */
export const projectStatusMap: Record<string, StatusConfig> = {
  draft: { label: 'Bản thảo', variant: 'default', icon: 'ri-draft-line' },
  planning: { label: 'Lập dự án', variant: 'info', icon: 'ri-layout-masonry-line' },
  designing: { label: 'Thiết kế', variant: 'purple', icon: 'ri-edit-2-line' },
  surveying: { label: 'Khảo sát hiện trạng', variant: 'info', icon: 'ri-compass-3-line' },
  mobilization: { label: 'Huy động thi công', variant: 'orange', icon: 'ri-truck-line' },
  construction: { label: 'Đang thi công', variant: 'orange', icon: 'ri-building-2-line' },
  rough_grading: { label: 'Tạo dáng sân vườn', variant: 'orange', icon: 'ri-landscape-line' },
  hardscaping: { label: 'Thi công hạ tầng', variant: 'orange', icon: 'ri-stack-line' },
  softscaping: { label: 'Trồng cây & thảm cỏ', variant: 'success', icon: 'ri-seedling-line' },
  qa_pending: { label: 'Chờ nghiệm thu', variant: 'purple', icon: 'ri-search-eye-line' },
  handover: { label: 'Bàn giao', variant: 'info', icon: 'ri-key-2-line' },
  maintenance: { label: 'Bảo dưỡng sau thi công', variant: 'success', icon: 'ri-shield-check-line' },
  warranty: { label: 'Bảo hành', variant: 'info', icon: 'ri-verified-badge-line' },
  active: { label: 'Đang hoạt động', variant: 'success', icon: 'ri-checkbox-circle-line' },
  paused: { label: 'Tạm dừng', variant: 'warning', icon: 'ri-pause-circle-line' },
  closed: { label: 'Đóng dự án', variant: 'secondary', icon: 'ri-door-lock-line' },
  cancelled: { label: 'Đã hủy', variant: 'danger', icon: 'ri-close-circle-line' },
  archived: { label: 'Lưu trữ', variant: 'secondary', icon: 'ri-archive-line' },
  // High-end landscape specific
  site_clearing: { label: 'Giải phóng mặt bằng', variant: 'secondary', icon: 'ri-seedling-line' },
  irrigation_install: { label: 'Thi công hệ thống tưới', variant: 'info', icon: 'ri-water-flash-line' },
  lighting_install: { label: 'Thi công chiếu sáng', variant: 'warning', icon: 'ri-lightbulb-line' },
  planting_large: { label: 'Trồng cây bóng mát', variant: 'success', icon: 'ri-tree-line' },
  planting_shrubs: { label: 'Trồng cây bụi & thảm cỏ', variant: 'success', icon: 'ri-leaf-line' },
  stone_work: { label: 'Thi công tiểu cảnh & đá', variant: 'purple', icon: 'ri-landscape-line' },
};

/** Contract status - Specialized for Construction & Maintenance */
export const contractStatusMap: Record<string, StatusConfig> = {
  draft: { label: 'Bản thảo', variant: 'default', icon: 'ri-draft-line' },
  pending_review: { label: 'Đang soát xét', variant: 'info', icon: 'ri-search-eye-line' },
  pending_approval: { label: 'Chờ phê duyệt', variant: 'warning', icon: 'ri-time-line' },
  active: { label: 'Đang hiệu lực', variant: 'success', icon: 'ri-checkbox-circle-line' },
  suspended: { label: 'Tạm dừng', variant: 'warning', icon: 'ri-pause-circle-line' },
  completed: { label: 'Đã hoàn thành', variant: 'success', icon: 'ri-check-double-line' },
  warranty: { label: 'Đang bảo hành', variant: 'info', icon: 'ri-shield-check-line' },
  near_expiry: { label: 'Sắp hết hạn', variant: 'orange', icon: 'ri-error-warning-line' },
  expired: { label: 'Hết hạn', variant: 'danger', icon: 'ri-close-circle-line' },
  terminated: { label: 'Chấm dứt', variant: 'secondary', icon: 'ri-stop-circle-line' },
  archived: { label: 'Lưu trữ', variant: 'secondary', icon: 'ri-archive-line' },
};

export const contractTypeMap: Record<string, StatusConfig> = {
  construction: { label: 'Thi công', variant: 'purple', icon: 'ri-building-2-line' },
  maintenance: { label: 'Bảo dưỡng', variant: 'success', icon: 'ri-shield-check-line' },
  lump_sum: { label: 'Trọn gói', variant: 'info', icon: 'ri-coins-line' },
  unit_price: { label: 'Theo đơn giá', variant: 'info', icon: 'ri-list-settings-line' },
};

/** Maintenance Package types */
export const packageTypeMap: Record<string, StatusConfig> = {
  basic: { label: 'Cơ bản', variant: 'default', icon: 'ri-medal-line' },
  standard: { label: 'Tiêu chuẩn', variant: 'info', icon: 'ri-medal-2-line' },
  premium: { label: 'Cao cấp', variant: 'purple', icon: 'ri-vip-crown-line' },
  custom: { label: 'Tùy chỉnh', variant: 'orange', icon: 'ri-tools-line' },
};

/** Utility Asset status */
export const utilityStatusMap: Record<string, StatusConfig> = {
  available: { label: 'Sẵn sàng', variant: 'success', icon: 'ri-checkbox-circle-line' },
  operating: { label: 'Đang vận hành', variant: 'success', icon: 'ri-play-circle-line' },
  warning: { label: 'Cần chú ý', variant: 'warning', icon: 'ri-error-warning-line' },
  maintenance: { label: 'Đang bảo trì', variant: 'warning', icon: 'ri-tools-line' },
  fault: { label: 'Có lỗi', variant: 'danger', icon: 'ri-close-circle-line' },
  offline: { label: 'Ngưng hoạt động', variant: 'secondary', icon: 'ri-plug-line' },
  retired: { label: 'Ngừng sử dụng', variant: 'default', icon: 'ri-archive-line' },
};

/** Material Request status */
export const requestStatusMap: Record<string, StatusConfig> = {
  draft: { label: 'Nháp', variant: 'secondary', icon: 'ri-draft-line' },
  pending: { label: 'Chờ duyệt', variant: 'info', icon: 'ri-time-line' },
  approved: { label: 'Đã duyệt', variant: 'success', icon: 'ri-check-double-line' },
  rejected: { label: 'Từ chối', variant: 'danger', icon: 'ri-close-circle-line' },
  delivered: { label: 'Đã giao', variant: 'purple', icon: 'ri-truck-line' },
};

/** Project Zone status */
export const zoneStatusMap: Record<string, StatusConfig> = {
  draft: { label: 'Bản thảo', variant: 'default', icon: 'ri-draft-line' },
  ready: { label: 'Sẵn sàng', variant: 'info', icon: 'ri-checkbox-circle-line' },
  in_progress: { label: 'Đang thi công', variant: 'orange', icon: 'ri-building-2-line' },
  blocked: { label: 'Vướng mắc', variant: 'danger', icon: 'ri-error-warning-line' },
  qa_pending: { label: 'Chờ nghiệm thu', variant: 'purple', icon: 'ri-search-eye-line' },
  accepted: { label: 'Đã nghiệm thu', variant: 'success', icon: 'ri-verified-badge-line' },
  completed: { label: 'Hoàn thành', variant: 'success', icon: 'ri-checkbox-circle-fill' },
  handover_ready: { label: 'Sẵn sàng bàn giao', variant: 'info', icon: 'ri-key-2-line' },
  active: { label: 'Đang duy trì', variant: 'success', icon: 'ri-shield-check-line' },
  maintenance: { label: 'Bảo dưỡng', variant: 'warning', icon: 'ri-tools-line' },
  warning: { label: 'Cần chú ý', variant: 'warning', icon: 'ri-alert-line' },
  inactive: { label: 'Ngưng hoạt động', variant: 'secondary', icon: 'ri-forbid-line' },
  archived: { label: 'Lưu trữ', variant: 'secondary', icon: 'ri-archive-line' },
};

/** BOQ status */
export const boqStatusMap: Record<string, StatusConfig> = {
  draft: { label: 'Bản thảo', variant: 'default', icon: 'ri-draft-line' },
  reviewing: { label: 'Đang soát xét', variant: 'info', icon: 'ri-search-eye-line' },
  approved: { label: 'Đã phê duyệt', variant: 'success', icon: 'ri-checkbox-circle-line' },
  locked: { label: 'Đã khóa', variant: 'secondary', icon: 'ri-lock-line' },
  revised: { label: 'Đã điều chỉnh', variant: 'orange', icon: 'ri-edit-box-line' },
  cancelled: { label: 'Đã hủy', variant: 'danger', icon: 'ri-close-circle-line' },
};

/** BOQ Item status */
export const boqItemStatusMap: Record<string, StatusConfig> = {
  planned: { label: 'Dự kiến', variant: 'default', icon: 'ri-calendar-line' },
  in_progress: { label: 'Đang thi công', variant: 'orange', icon: 'ri-building-2-line' },
  partially_done: { label: 'Hoàn thành một phần', variant: 'info', icon: 'ri-list-check' },
  completed: { label: 'Đã hoàn thành', variant: 'success', icon: 'ri-check-double-line' },
  changed: { label: 'Có thay đổi', variant: 'warning', icon: 'ri-error-warning-line' },
  cancelled: { label: 'Đã hủy', variant: 'danger', icon: 'ri-close-circle-line' },
};

/** Audit Log Action types */
export const auditActionMap: Record<string, StatusConfig> = {
  create: { label: 'Tạo mới', variant: 'success', icon: 'ri-add-circle-line' },
  update: { label: 'Cập nhật', variant: 'info', icon: 'ri-edit-line' },
  delete: { label: 'Xóa', variant: 'danger', icon: 'ri-delete-bin-line' },
  login: { label: 'Đăng nhập', variant: 'info', icon: 'ri-login-box-line' },
  logout: { label: 'Đăng xuất', variant: 'secondary', icon: 'ri-logout-box-line' },
  download: { label: 'Tải về', variant: 'purple', icon: 'ri-download-line' },
  upload: { label: 'Tải lên', variant: 'info', icon: 'ri-upload-line' },
  permission_change: { label: 'Đổi quyền', variant: 'warning', icon: 'ri-shield-keyhole-line' },
};

/** Landscape Zone Types */
export const zoneTypeMap: Record<string, StatusConfig> = {
  entrance_landscape: { label: 'Cảnh quan lối vào', variant: 'default', icon: 'ri-door-open-line' },
  roadside_landscape: { label: 'Cảnh quan lề đường', variant: 'default', icon: 'ri-road-map-line' },
  central_garden: { label: 'Vườn trung tâm', variant: 'default', icon: 'ri-tree-line' },
  villa_garden: { label: 'Sân vườn biệt thự', variant: 'default', icon: 'ri-home-4-line' },
  podium_landscape: { label: 'Cảnh quan khối đế', variant: 'default', icon: 'ri-building-line' },
  park_area: { label: 'Khu vực công viên', variant: 'default', icon: 'ri-landscape-line' },
  lake_edge: { label: 'Ven hồ', variant: 'default', icon: 'ri-drop-line' },
  playground: { label: 'Khu vui chơi', variant: 'default', icon: 'ri-mickey-line' },
  rooftop_garden: { label: 'Vườn mái', variant: 'default', icon: 'ri-building-2-line' },
  irrigation_zone: { label: 'Khu vực tưới', variant: 'default', icon: 'ri-water-flash-line' },
  lighting_zone: { label: 'Khu vực chiếu sáng', variant: 'default', icon: 'ri-lightbulb-line' },
  nursery_zone: { label: 'Khu vực vườn ươm', variant: 'default', icon: 'ri-leaf-line' },
  hardscape_zone: { label: 'Khu vực hạ tầng cứng', variant: 'default', icon: 'ri-stack-line' },
};

// --- Helper Functions ---

/**
 * Generic function to get status config from any map
 * Falls back to a default config if not found
 */
export const getStatusConfig = (
  status: string,
  map: Record<string, StatusConfig> = woStatusMap
): StatusConfig => {
  const s = status?.toLowerCase() || '';
  return map[s] || { label: status, variant: 'default', icon: 'ri-question-line' };
};

/** Specialized helpers for common use cases */
export const getAccountStatus = (status: string) => getStatusConfig(status, accountStatusMap);
export const getWorkStatus = (status: string) => getStatusConfig(status, workStatusMap);
export const getWOStatus = (status: string) => getStatusConfig(status, woStatusMap);
export const getUtilityStatus = (status: string) => getStatusConfig(status, utilityStatusMap);
export const getApprovalStatus = (status: string) => getStatusConfig(status, approvalStatusMap);
export const getRequestStatus = (status: string) => getStatusConfig(status, requestStatusMap);
export const getProjectStatus = (status: string) => getStatusConfig(status, projectStatusMap);
export const getAuditAction = (action: string) => getStatusConfig(action, auditActionMap);
export const getContractStatus = (status: string) => getStatusConfig(status, contractStatusMap);
export const getContractType = (type: string) => getStatusConfig(type, contractTypeMap);
export const getPackageType = (type: string) => getStatusConfig(type, packageTypeMap);
export const getZoneStatus = (status: string) => getStatusConfig(status, zoneStatusMap);
export const getZoneType = (type: string) => getStatusConfig(type, zoneTypeMap);

/**
 * Intelligent inference based on common status keywords
 * Used by UI components like StatusBadge
 */
export const inferStatusConfig = (status: string): StatusConfig => {
  const s = status?.toLowerCase() || '';

  if (s.includes('hoàn thành') || s.includes('đã duyệt') || s.includes('xong') || s.includes('success') || s.includes('active') || s.includes('hoạt động') || s.includes('available') || s.includes('operating') || s.includes('sẵn sàng') || s.includes('vận hành') || s.includes('approved') || s.includes('dispatched')) {
    return { label: status, variant: 'success', icon: 'ri-checkbox-circle-line' };
  }
  if (s.includes('chờ') || s.includes('pending') || s.includes('đang xử lý') || s.includes('in_progress') || s.includes('warning') || s.includes('maintenance') || s.includes('bảo trì') || s.includes('planning') || s.includes('designing') || s.includes('construction') || s.includes('thi công') || s.includes('quy hoạch') || s.includes('thiết kế') || s.includes('processing') || s.includes('submitted')) {
    return { label: status, variant: 'warning', icon: 'ri-time-line' };
  }
  if (s.includes('từ chối') || s.includes('hủy') || s.includes('lỗi') || s.includes('danger') || s.includes('khóa') || s.includes('locked') || s.includes('quá hạn') || s.includes('fault') || s.includes('closed') || s.includes('cancelled')) {
    return { label: status, variant: 'danger', icon: 'ri-error-warning-line' };
  }
  if (s.includes('mới') || s.includes('new') || s.includes('open') || s.includes('mở') || s.includes('info') || s.includes('handover') || s.includes('bàn giao')) {
    return { label: status, variant: 'info', icon: 'ri-information-line' };
  }

  return { label: status, variant: 'default', icon: 'ri-question-line' };
};
